import { useMemo, useState } from "react";
import { Box, Menu, MenuItem, Stack, Typography } from "@mui/material";
import ExpandIcon from "@mui/icons-material/ExpandMoreRounded";
import EmptyIcon from "@mui/icons-material/InboxRounded";

import Card3D from "./Card3D";
import { money, num, tone } from "../api";
import type { Trade } from "../api";

interface DeployedAlgosProps {
  open: Trade[];
  closed: Trade[];
}

type FilterKey = "status" | "strategy" | "direction";

const STATUS = ["All", "Open", "Closed"];
const DIRECTION = ["All", "LONG", "SHORT"];

/** A single dropdown filter chip. */
function FilterChip({
  label,
  value,
  options,
  onPick,
}: {
  label: string;
  value: string;
  options: string[];
  onPick: (v: string) => void;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const active = value !== "All";

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        gap={0.7}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          px: 1.5,
          py: 0.85,
          borderRadius: "9px",
          cursor: "pointer",
          fontSize: ".78rem",
          fontWeight: 700,
          userSelect: "none",
          color: active ? "var(--blue)" : "var(--text)",
          background: "linear-gradient(180deg,#131c28,#0b1119)",
          border: `1px solid ${active ? "#1d3a5c" : "var(--border)"}`,
          boxShadow: "inset 0 1px 0 rgba(255,255,255,.06), 0 2px 6px rgba(0,0,0,.4)",
          transition: "border-color 160ms ease",
          "&:hover": { borderColor: "var(--border-2)" },
        }}
      >
        {active ? value : label}
        <ExpandIcon sx={{ fontSize: 17, color: "var(--muted)" }} />
      </Stack>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 190, mt: 0.6 } } }}
      >
        {options.map((o) => (
          <MenuItem
            key={o}
            selected={o === value}
            onClick={() => {
              onPick(o);
              setAnchor(null);
            }}
            sx={{ fontSize: ".82rem", fontWeight: 600 }}
          >
            {o}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export default function DeployedAlgos({ open, closed }: DeployedAlgosProps) {
  const [filters, setFilters] = useState<Record<FilterKey, string>>({
    status: "Open",
    strategy: "All",
    direction: "All",
  });

  const strategies = useMemo(() => {
    const set = new Set([...open, ...closed].map((t) => t.strategy_label));
    return ["All", ...[...set].sort()];
  }, [open, closed]);

  const rows = useMemo(() => {
    let pool: Trade[] =
      filters.status === "Open"
        ? open
        : filters.status === "Closed"
          ? closed
          : [...open, ...closed];

    if (filters.strategy !== "All") {
      pool = pool.filter((t) => t.strategy_label === filters.strategy);
    }
    if (filters.direction !== "All") {
      pool = pool.filter((t) => t.direction === filters.direction);
    }
    return pool;
  }, [open, closed, filters]);

  const set = (k: FilterKey) => (v: string) =>
    setFilters((f) => ({ ...f, [k]: v }));

  return (
    <Card3D intensity={3}>
      <Box sx={{ p: 2.5, height: "100%", display: "flex", flexDirection: "column" }}>
        <Stack
          direction="row"
          alignItems="center"
          gap={1.2}
          flexWrap="wrap"
          className="layer-1"
        >
          <Typography sx={{ fontSize: "1.02rem", fontWeight: 800, mr: "auto" }}>
            Deployed Algos
          </Typography>
          <FilterChip
            label="Status"
            value={filters.status}
            options={STATUS}
            onPick={set("status")}
          />
          <FilterChip
            label="Strategy Type"
            value={filters.strategy}
            options={strategies}
            onPick={set("strategy")}
          />
          <FilterChip
            label="Direction"
            value={filters.direction}
            options={DIRECTION}
            onPick={set("direction")}
          />
        </Stack>

        {rows.length === 0 ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            gap={1.2}
            sx={{ flex: 1, minHeight: 240, color: "var(--muted)" }}
          >
            <Box
              sx={{
                width: 58,
                height: 58,
                borderRadius: "16px",
                display: "grid",
                placeItems: "center",
                background: "linear-gradient(145deg,#151f2c,#0b1119)",
                border: "1px solid var(--border)",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,.06)",
              }}
            >
              <EmptyIcon sx={{ fontSize: 27, color: "var(--muted)" }} />
            </Box>
            <Typography sx={{ fontSize: ".88rem", fontWeight: 700 }}>
              No algos deployed
            </Typography>
            {/* Described the retired Gold Digger's entry rules until 09-Aug-2026,
                which made an empty table look like a filter problem rather than
                the deliberate state it is. */}
            <Typography sx={{ fontSize: ".76rem", textAlign: "center", maxWidth: 360 }}>
              No strategy is currently allowed to trade. The Renko family was
              retired after testing and nothing has been promoted in its place,
              so the engine scans and scores but places no orders.
            </Typography>
          </Stack>
        ) : (
          <Box sx={{ mt: 2, overflowX: "auto", flex: 1 }}>
            <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
              <Box component="thead">
                <Box component="tr">
                  {[
                    "Instrument",
                    "Strategy",
                    "Dir",
                    "Qty",
                    "Entry",
                    "LTP",
                    "Target / Stop",
                    "P&L",
                  ].map((h) => (
                    <Box
                      key={h}
                      component="th"
                      sx={{
                        textAlign: h === "P&L" ? "right" : "left",
                        fontSize: ".62rem",
                        fontWeight: 800,
                        letterSpacing: ".07em",
                        textTransform: "uppercase",
                        color: "var(--muted)",
                        py: 1,
                        px: 1,
                        borderBottom: "1px solid var(--border)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </Box>
                  ))}
                </Box>
              </Box>

              <Box component="tbody">
                {rows.map((t) => {
                  const pt = tone(t.pnl);
                  return (
                    <Box
                      component="tr"
                      key={t.id}
                      sx={{
                        transition: "background 140ms ease",
                        "&:hover": { background: "#0e1622" },
                      }}
                    >
                      <Box component="td" sx={cellSx}>
                        <Stack direction="row" alignItems="center" gap={0.9}>
                          <Box
                            sx={{
                              fontSize: ".62rem",
                              fontWeight: 800,
                              px: 0.75,
                              py: 0.25,
                              borderRadius: "5px",
                              color: t.option === "CE" ? "var(--green)" : "var(--red)",
                              background:
                                t.option === "CE" ? "#07281a" : "#2a0d0d",
                              border: `1px solid ${t.option === "CE" ? "#1c5c39" : "#5c1a1a"}`,
                            }}
                          >
                            {t.option}
                          </Box>
                          <Box>
                            <Typography sx={{ fontSize: ".82rem", fontWeight: 800 }}>
                              {t.instrument}
                            </Typography>
                            <Typography
                              sx={{ fontSize: ".64rem", color: "var(--muted)" }}
                            >
                              {t.symbol} · {t.lots} lot{t.lots === 1 ? "" : "s"}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>

                      <Box component="td" sx={cellSx}>
                        <Typography
                          sx={{ fontSize: ".76rem", fontWeight: 700, color: "var(--amber)" }}
                        >
                          {t.strategy_label}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: ".64rem",
                            color: "var(--muted)",
                            maxWidth: 210,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {t.reason}
                        </Typography>
                      </Box>

                      <Box component="td" sx={cellSx}>
                        <Typography
                          sx={{
                            fontSize: ".72rem",
                            fontWeight: 800,
                            color:
                              t.direction === "LONG" ? "var(--green)" : "var(--red)",
                          }}
                        >
                          {t.direction}
                        </Typography>
                      </Box>

                      <Box component="td" sx={{ ...cellSx }} className="mono">
                        {t.qty}
                      </Box>
                      <Box component="td" sx={cellSx} className="mono">
                        ₹{num(t.entry_price)}
                      </Box>
                      <Box component="td" sx={cellSx} className="mono">
                        ₹{num(t.status === "CLOSED" ? t.exit_price : t.ltp)}
                      </Box>

                      <Box component="td" sx={cellSx}>
                        <Typography className="mono" sx={{ fontSize: ".7rem" }}>
                          <span style={{ color: "var(--green)" }}>
                            {num(t.target_underlying, 0)}
                          </span>{" "}
                          /{" "}
                          <span style={{ color: "var(--red)" }}>
                            {num(t.stop_underlying, 0)}
                          </span>
                        </Typography>
                        {t.status === "CLOSED" && (
                          <Typography
                            sx={{
                              fontSize: ".6rem",
                              fontWeight: 800,
                              color: "var(--muted)",
                            }}
                          >
                            {t.exit_reason}
                          </Typography>
                        )}
                      </Box>

                      <Box
                        component="td"
                        sx={{ ...cellSx, textAlign: "right" }}
                      >
                        <Typography
                          className={`mono ${pt}`}
                          sx={{ fontSize: ".86rem", fontWeight: 800 }}
                        >
                          {money(t.pnl, true)}
                        </Typography>
                        <Typography
                          className={`mono ${pt}`}
                          sx={{ fontSize: ".64rem", opacity: 0.8 }}
                        >
                          {t.pnl_pct > 0 ? "+" : ""}
                          {t.pnl_pct.toFixed(1)}%
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Card3D>
  );
}

const cellSx = {
  py: 1.2,
  px: 1,
  borderBottom: "1px solid var(--border)",
  fontSize: ".8rem",
  verticalAlign: "middle" as const,
  whiteSpace: "nowrap" as const,
};
